import Vue from "vue";
import VueRouter, { RawLocation, Route } from "vue-router";
import OrdersRoutes from "@/routes/orders";
import GeneralRoutes from "@/routes/general";
import NotFoundView from "@/views/NotFoundView.vue";
import { vuexStore } from "@/store";
import { CustomRoute, CustomRouteConfig } from "@/models/plugins/CustomRouterTypes";
import BookingsRoutes from "@/routes/bookings";
import getOfficeNameFromCurrentHref, { getRouteWithoutCompanyFromCurrentHref } from "@/utils/getParamsFromPath";
import AdministrationRoutes from "@/routes/administration";
import BookKeepingRoutes from "@/routes/bookkeeping";
import DeveloperRoutes from "@/routes/developer";
import DocumentsRoutes from "@/routes/documents";
import FinancesRoutes from "@/routes/finances";
import ReportsRoutes from "@/routes/reports";
import CostDocumentsRoutes from "@/routes/costDocuments";
import Costs from "@/routes/costs";
import { CommercialCostsRoutes } from "@/routes/commercialCost";

//ErrorService
import errorService from "@/plugins/errors";

export class CustomVueRouter extends VueRouter {
  public getCurrentOfficeShortname = (): string | undefined => {
    return getOfficeNameFromCurrentHref();
  };

  private addDefaultCurrentOfficeShortNameToLocation(location: RawLocation): RawLocation {
    if (typeof location === "string") {
      location = "/" + this.getCurrentOfficeShortname() + "/" + location;
    } else {
      if (typeof location.params != "object") {
        location.params = {};
      }
      const currentOfficeShortName = this.getCurrentOfficeShortname();
      if (typeof location.params?.officeShortName != "string" && typeof currentOfficeShortName === "string") {
        location.params.officeShortName = currentOfficeShortName;
      }
    }
    return location;
  }

  async push(location: RawLocation): Promise<Route> {
    const locationWithOfficeShortName = this.addDefaultCurrentOfficeShortNameToLocation(location);
    return await super.push(locationWithOfficeShortName).catch((error) => {
      errorService.onVueRouterError(error);
      return error;
    });
  }
  async replace(location: RawLocation): Promise<Route> {
    const locationWithOfficeShortName = this.addDefaultCurrentOfficeShortNameToLocation(location);
    return await super.replace(locationWithOfficeShortName).catch((error) => {
      errorService.onVueRouterError(error);
      return error;
    });
  }
}

Vue.use(CustomVueRouter);

const generalRoutes: Array<CustomRouteConfig> = [];

const routes: Array<CustomRouteConfig> = generalRoutes
  .concat(GeneralRoutes)
  .concat(BookKeepingRoutes)
  .concat(BookingsRoutes)
  .concat(OrdersRoutes)
  .concat(AdministrationRoutes)
  .concat(DeveloperRoutes)
  .concat(DocumentsRoutes)
  .concat(CostDocumentsRoutes)
  .concat(Costs)
  .concat(FinancesRoutes)
  .concat(ReportsRoutes)
  .concat(CommercialCostsRoutes)
  .concat([
    {
      name: "NotFound",
      path: "*",
      component: NotFoundView,
      meta: { isAnonymousAccess: true },
    },
  ]);

for (const route of routes) {
  if (route.meta === undefined || route.meta.skipOfficeInUrl !== true) {
    route.path = "/:officeShortName/" + route.path;
  }
}

const routerInstance = new CustomVueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

export const hasPermissionForRoute = async (route: CustomRoute): Promise<boolean> => {
  if (typeof route.meta !== "undefined" && typeof route.meta.hasOneOfPermissions !== "undefined") {
    return await vuexStore.dispatch.user.hasOneOfPermission(route.meta.hasOneOfPermissions);
  }
  return true;
};

routerInstance.beforeEach(async (to: CustomRoute, from: CustomRoute, next) => {
  if (vuexStore.getters.offices.isOfficesDownloaded) {
    if (
      typeof to.meta !== "undefined" &&
      typeof to.meta.isAnonymousAccess !== "undefined" &&
      !to.meta.isAnonymousAccess &&
      !vuexStore.getters.user.isLogged
    ) {
      await next({
        name: "Login",
        params: {
          officeShortName: getOfficeNameFromCurrentHref(),
          redirect: encodeURIComponent(getRouteWithoutCompanyFromCurrentHref()),
        },
        query: to.query,
        replace: true,
      });
    }
  }
  if (vuexStore.getters.user.isPermissionsDownloaded) {
    const hasPermission = await hasPermissionForRoute(to);
    if (!hasPermission) {
      await next({
        name: "Insufficient Permissions",
        params: { officeShortName: getOfficeNameFromCurrentHref() },
      });
    }
  }
  await next();
});

// To handle errors during navigation - navigationGuard
routerInstance.onError((error) => {
  // TODO: hkopczacki - remove after tests
  console.log("router.onError: ", error);

  if (/loading chunk \d* failed./i.test(error.message)) {
    window.location.reload();
  }
});

export { routerInstance };
export default routerInstance;
